/**
 * Leave Approvals — the queue managers and HR act on.
 *
 * This page is the missing half of the leave lifecycle. Before it existed, nothing in the
 * app ever called leaveApprovalService.updateLeaveStatus, so every request submitted since
 * the module shipped sat at "Submitted" forever and no approval could reach anyone else's
 * calendar.
 *
 * Modelled on TeamTimesheets so the two approval queues behave the same way: status tabs,
 * an employee filter, a date range, and per-row actions.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { LeaveService } from '../../../services/leave';
import { LeaveApprovalService } from '../../../services/leaveApprovalService';
import { LeaveRequest, LeaveStatus } from '../../../types/leave';
import { loadAllUsers, loadDirectReports, SimpleUser } from '../../../services/userLoader';
import { resolveActorId, sameIdentity } from '../../../services/identity';
import { canDecideLeave, isOrgWideViewer } from '../../../services/leaveVisibility';
import { leaveDaysFor } from '../../../services/leaveDuration';
import { notifyLeaveDecision } from '../../../services/inAppNotification';
import EmptyState from '../../common/EmptyState';
import '../LeavePage.css';
import './leavePages.css';
import '../../common/emsPage.css';

type FilterKey = 'All' | LeaveStatus;

/*
 * No icons. Each chip carried an emoji — 📋 ⏳ ✅ ❌ 🔄 — and every one of them said what the
 * word beside it already said, in a glyph that renders differently on each platform and that a
 * screen reader announces as its own sentence ahead of the label. The status name and the count
 * are the whole content of these chips.
 */
const FILTERS: { key: FilterKey; label: string }[] = [
  // Labels are the status values themselves. SUBMITTED was labelled "Pending", which is not
  // a status the system has — so this chip read "Pending" while every row it filtered showed
  // "Submitted" in its Status column.
  { key: 'All', label: 'All' },
  { key: LeaveStatus.SUBMITTED, label: LeaveStatus.SUBMITTED },
  { key: LeaveStatus.APPROVED, label: LeaveStatus.APPROVED },
  { key: LeaveStatus.REJECTED, label: LeaveStatus.REJECTED },
  { key: LeaveStatus.CANCELLATION_REQUESTED, label: LeaveStatus.CANCELLATION_REQUESTED },
];

// Who counts as an approver, and which requests they may act on, now comes from
// leaveVisibility — see isLeaveApprover / canDecideLeave there. This page used to keep its
// own ADMIN_ROLES list, which is how the app ended up with three different answers to
// "who can approve leave" depending on the screen.

// Deliberately the same helper the balance uses, so the figure an approver sees is exactly
// what will be deducted — weekends and public holidays excluded, half-days charged as 0.5.
const dayCount = (leave: LeaveRequest): number => leaveDaysFor(leave);

const sessionLabel = (session?: string): string => {
  if (session === 'HALF_AM') return 'Half day (AM)';
  if (session === 'HALF_PM') return 'Half day (PM)';
  return 'Full day';
};

const LeaveApprovalsPage: React.FC = () => {
  const { user } = useAuth();

  const [leaves, setLeaves] = useState<LeaveRequest[]>([]);
  const [reports, setReports] = useState<SimpleUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterKey>('All');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [actionOn, setActionOn] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const isAdmin = isOrgWideViewer({ email: user?.email, role: user?.role });

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3200);
  };

  /** Just the leave requests — cheap, and the only thing that actually changes between focuses. */
  const loadLeaves = useCallback(async () => {
    try {
      setLeaves(await LeaveService.listLeaves());
    } catch (error) {
      console.error('[LeaveApprovals] leave refresh failed:', error);
    }
  }, []);

  const loadData = useCallback(async () => {
    // Admins act on everyone and never need their own email for that (loadAllUsers takes
    // none), so only gate on the user existing at all — some federated SSO admin accounts
    // (Azure AD SAML) have no email attribute, and requiring one here left them stuck on
    // "Loading leave requests..." forever. A manager still needs their own email to look up
    // direct reports; if that's ever missing they simply see no reports, same as before.
    if (!user?.id) return;
    setLoading(true);
    try {
      // Admins act on everyone; a manager only ever sees their own reports, so the
      // directory lookup doubles as the authorisation boundary. The directory rarely
      // changes, so it is only fetched here (mount / role change) — not on every focus.
      const [allLeaves, directory] = await Promise.all([
        LeaveService.listLeaves(),
        isAdmin ? loadAllUsers() : (user.email ? loadDirectReports(user.email) : Promise.resolve([])),
      ]);
      setReports(directory);
      setLeaves(allLeaves);
    } catch (error) {
      console.error('[LeaveApprovals] load failed:', error);
      setLeaves([]);
    } finally {
      setLoading(false);
    }
  }, [user?.id, user?.email, isAdmin]);

  useEffect(() => { loadData(); }, [loadData]);

  /**
   * Refetch on focus / after a decision — leave requests only.
   *
   * Subscriptions are disabled app-wide, so nothing can push a new request into an already
   * open queue. Reloading on focus is the honest substitute: a manager who switches back to
   * this tab sees what has arrived since they left. This previously re-fetched the whole
   * employee directory (a full Cognito listUsers call) on every focus even though the
   * directory almost never changes between focuses — that was the main source of the slow
   * reload; only the leave list needs to be current.
   */
  useEffect(() => {
    window.addEventListener('focus', loadLeaves);
    window.addEventListener('leave-requests-updated', loadLeaves);
    return () => {
      window.removeEventListener('focus', loadLeaves);
      window.removeEventListener('leave-requests-updated', loadLeaves);
    };
  }, [loadLeaves]);

  /**
   * Requests this viewer is allowed to act on.
   *
   * Delegates to canDecideLeave so a Manager, HR Admin and Super Admin are all judged by one
   * rule: org-wide roles get everyone, a manager gets their reports. Previously this branched
   * on a local role list, which is why the answer could differ from the sidebar's.
   */
  const actionable = useMemo(() => {
    if (!user?.id) return [];
    const viewer = {
      email: user.email,
      role: user.role,
      reportEmails: reports.map(r => r.email),
    };
    return leaves.filter(l => canDecideLeave(l, viewer));
  }, [leaves, reports, user?.id, user?.email, user?.role]);

  const counts = useMemo(() => {
    const tally: Record<string, number> = { All: actionable.length };
    FILTERS.forEach(f => {
      if (f.key !== 'All') tally[f.key] = actionable.filter(l => l.status === f.key).length;
    });
    return tally;
  }, [actionable]);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return actionable
      .filter(l => (filter === 'All' ? true : l.status === filter))
      .filter(l => !term
        || (l.employeeName || '').toLowerCase().includes(term)
        || (l.employeeEmail || '').toLowerCase().includes(term))
      .filter(l => !dateFrom || l.endDate >= dateFrom)
      .filter(l => !dateTo || l.startDate <= dateTo)
      .sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
  }, [actionable, filter, search, dateFrom, dateTo]);

  const decide = async (leave: LeaveRequest, status: LeaveStatus) => {
    /*
     * The actor is resolved, not assumed to be an email.
     *
     * This used to be `if (!user?.email) return;` — a silent no-op. A federated Azure AD admin
     * account can carry no email attribute at all, so an HR Admin or Super Admin clicking
     * Approve got nothing: no decision, no error, no clue why. loadData above already had this
     * exact bug fixed for the same reason; this guard was missed.
     *
     * Now it only refuses when the user cannot be identified at all, and says so.
     */
    const actorId = resolveActorId(user);
    if (!actorId) {
      showToast('Could not identify your account, so the decision was not recorded. Please sign in again.', 'error');
      return;
    }

    let note: string | undefined;
    if (status === LeaveStatus.REJECTED) {
      // A rejection without a reason is unhelpful to the employee, so require one.
      const entered = window.prompt(`Reason for rejecting ${leave.employeeName}'s leave:`);
      if (entered === null) return;
      if (!entered.trim()) { showToast('A reason is required to reject a request.', 'error'); return; }
      note = entered.trim();
    }

    setActionOn(leave.id);
    try {
      const updated = await LeaveApprovalService.updateLeaveStatus(leave.id, status, actorId, note);
      if (!updated) { showToast('Could not update the request. Please retry.', 'error'); return; }

      const approved = status === LeaveStatus.APPROVED;
      const dateRange = leave.startDate === leave.endDate
        ? leave.startDate
        : `${leave.startDate} to ${leave.endDate}`;
      const approverName = user?.name || actorId;

      // Notifications must never block the decision — it is already persisted, and a mail
      // failure should not make a successful approval look like it failed.
      notifyLeaveDecision(
        leave.employeeEmail, leave.employeeName, approverName,
        String(leave.type), dateRange, approved, actorId, note
      ).catch(err => console.warn('[LeaveApprovals] in-app notification failed:', err));

      /*
       * The decision EMAIL is not sent from here any more.
       *
       * updateLeaveStatus sends it, immediately after the write it depends on, so the rule
       * "email only once the status really changed" is enforced by the code that changes the
       * status rather than by every screen that asks it to. Calling it here as well would send
       * the employee, their manager and HR two of each.
       */

      showToast(`${leave.employeeName}'s leave ${approved ? 'approved' : 'rejected'}.`, 'success');
      await loadLeaves();
    } catch (error: any) {
      showToast(error?.message || 'Failed to update the request.', 'error');
    } finally {
      setActionOn(null);
    }
  };

  if (!user) return null;

  return (
    <section className="la-team-page">
      {/* ── Page Header ── */}
      <div className="la-team-header">
        <div>
          <h2 className="la-team-title">Leave Approvals</h2>
          <p className="la-team-subtitle">
            {isAdmin
              ? 'Review and manage all employee leave requests across departments.'
              : `Review and manage leave requests from your ${reports.length} direct report(s).`}
          </p>
        </div>
        <button
          type="button"
          className="la-refresh-btn"
          onClick={loadData}
          disabled={loading}
        >
          {loading ? 'Refreshing…' : '🔄 Refresh'}
        </button>
      </div>

      {toast && <div className={`sr-toast sr-toast--${toast.type}`}>{toast.message}</div>}

      {/* ── Status Filters (No outer container box!) ── */}
      <div className="la-status-filters">
        {FILTERS.map(f => (
          <button
            key={String(f.key)}
            type="button"
            className={`la-filter-chip ${filter === f.key ? 'active' : ''}`}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
            <span className="la-filter-badge">{counts[String(f.key)] ?? 0}</span>
          </button>
        ))}
      </div>

      {/* ── Search & Date Filters Row ── */}
      <div className="la-filter-row">
        <div className="la-search-container">
          <input
            type="text"
            className="la-search-input"
            placeholder="🔍 Search employee by name or email…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div className="la-date-range">
          <label className="la-date-label">From:</label>
          <input
            type="date"
            className="la-date-input"
            value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
          />
          <label className="la-date-label">To:</label>
          <input
            type="date"
            className="la-date-input"
            value={dateTo}
            onChange={e => setDateTo(e.target.value)}
          />
          {(search || dateFrom || dateTo) && (
            <button
              type="button"
              className="la-date-clear"
              onClick={() => { setSearch(''); setDateFrom(''); setDateTo(''); }}
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* ── Content Card (Encloses only list / empty state) ── */}
      <div className="la-content-card">
        {loading ? (
          <div className="la-loading-state">
            <p>Loading leave requests…</p>
          </div>
        ) : !isAdmin && reports.length === 0 ? (
          <EmptyState
            icon="inbox"
            title="No direct reports"
            subtitle="Leave approvals appear here once employees report to you. Ask HR to set your email as their Reports To."
          />
        ) : visible.length === 0 ? (
          <EmptyState
            icon="inbox"
            title={filter === LeaveStatus.SUBMITTED ? 'Nothing awaiting approval' : 'No matching requests found'}
            subtitle={
              filter === LeaveStatus.SUBMITTED
                ? 'New leave requests will appear here as soon as employees submit them.'
                : 'Try adjusting your status filter or clearing search keywords.'
            }
          />
        ) : (
          <div className="la-table-wrapper">
            <table className="la-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Leave Type</th>
                  <th>Dates & Session</th>
                  <th>Days</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {visible.map(l => {
                  const isPending = l.status === LeaveStatus.SUBMITTED;
                  const busy = actionOn === l.id;
                  const isExpanded = expandedId === l.id;
                  return (
                    <React.Fragment key={l.id}>
                      <tr className={isExpanded ? 'la-row-expanded' : undefined}>
                        <td>
                          <div className="la-emp-info">
                            <strong>{l.employeeName}</strong>
                            <span className="la-emp-email">{l.employeeEmail}</span>
                          </div>
                        </td>
                        <td>
                          <span className="la-leave-type-badge">{l.type}</span>
                        </td>
                        <td className="la-dates">
                          <span>{l.startDate === l.endDate ? l.startDate : `${l.startDate} → ${l.endDate}`}</span>
                          <span className="la-session">{sessionLabel(l.session)}</span>
                        </td>
                        <td className="la-days">
                          <strong>{dayCount(l)}</strong> day(s)
                        </td>
                        <td>
                          <span className={`lb-status-badge status-${String(l.status).toLowerCase()}`}>
                            {l.status}
                          </span>
                        </td>
                        <td>
                          <div className="la-actions">
                            {isPending ? (
                              <>
                                <button
                                  type="button"
                                  className="la-btn-approve"
                                  disabled={busy}
                                  onClick={() => decide(l, LeaveStatus.APPROVED)}
                                >
                                  {busy ? '…' : 'Approve'}
                                </button>
                                <button
                                  type="button"
                                  className="la-btn-reject"
                                  disabled={busy}
                                  onClick={() => decide(l, LeaveStatus.REJECTED)}
                                >
                                  Reject
                                </button>
                              </>
                            ) : null}
                            <button
                              type="button"
                              className="la-btn-expand"
                              aria-expanded={isExpanded}
                              onClick={() => setExpandedId(isExpanded ? null : l.id)}
                            >
                              {isExpanded ? '▾' : '▸'}
                            </button>
                          </div>
                        </td>
                      </tr>

                      {isExpanded && (
                        <tr className="la-detail-row">
                          <td colSpan={6}>
                            <div className="la-detail">
                              <div><span className="la-detail-label">Department:</span> {l.department || 'General'}</div>
                              <div><span className="la-detail-label">Applied On:</span> {l.createdAt ? new Date(l.createdAt).toLocaleString() : '—'}</div>
                              <div className="la-detail-reason">
                                <span className="la-detail-label">Reason for Leave:</span>
                                <p>{l.reason || 'No reason provided.'}</p>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
};

export default LeaveApprovalsPage;
